import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { getNetPriceTotal, getDeliverySetupLines } from '~/utils/utils';
import { ISalesOrderResponseProduct } from '~/interfaces/get-sales-order';
import {
  DELIVERY_SEGMENT,
  DONATION_DELIVERY_PRODUCT_TYPE,
  DONATION_SEGMENT,
  WARRANTY_SEGMENT,
} from '~/constant/constants';
import { BlockedState } from '~/gql/__generated__/types';
import {
  PROGRESSIVE_LINE_TYPE_LEASE,
  PROGRESSIVE_LINE_TYPE_DELIVERY,
  PROGRESSIVE_LINE_SKU_DELIVERY,
  PROGRESSIVE_LINE_DESC_DELIVERY,
  PROGRESSIVE_LINE_DESC_WARRANTY,
} from '../constants';
/**
 * Calculates the total amount for the checkout cart, adjusting for delivery fees based on state restrictions.
 *
 * @param {ISalesOrderResponse} checkoutCart - The shopping cart object containing subtotal and delivery fee information.
 * @param {string[]} deliveryStates - List of states where delivery fee is not allowed.
 * @returns {number} - The total amount after adjusting for delivery fees if applicable.
 */
export const getProgressiveTotal = (
  checkoutCart: ISalesOrderResponse,
  deliveryStates: string[] | null | undefined,
  isProgressiveCart: boolean = false,
): number => {
  const state = checkoutCart?.ISOStateCode;
  let totalAmount = checkoutCart.TotalAmount;

  // delivery fee and recycling fee is not included in estimateAPI but is included in progresstive cart
  if (!isProgressiveCart) {
    // empty deliverystates mean, subtraction of delivery fee is not required
    if (deliveryStates !== null && deliveryStates !== undefined) {
      const deliveryArray = getDeliverySetupLines(checkoutCart?.ProductList);
      const deliveryPrice = getNetPriceTotal(deliveryArray);
      // Check if delivery fee is not allowed in the current state
      if (isDeliveryExcludedState(deliveryStates, state)) {
        const deliveryFee = deliveryPrice || 0;
        totalAmount -= deliveryFee;
      }
    }

    if (checkoutCart.TotalRecyclingFee > 0) {
      totalAmount -= checkoutCart.TotalRecyclingFee;
    }
  }
  if (checkoutCart.TotalTax > 0) {
    totalAmount -= checkoutCart.TotalTax;
  }

  return Math.round(totalAmount * 100) / 100;
};

/**
 * Encodes a given SSN string into a hexadecimal representation of its base64 encoding.
 *
 * @param {string} ssn - The Social Security Number (SSN) to be encoded.
 * @returns {string} The hexadecimal representation of the base64 encoded SSN.
 */
export const encodedSSN = (ssn: string): string => {
  // Convert to base64
  const base64String = btoa(ssn);

  // Convert base64 to hexadecimal
  let hex = '';
  for (let i = 0; i < base64String?.length; i++) {
    const charCode = base64String?.charCodeAt(i);
    const hexValue = charCode?.toString(16);
    // Pad with zeros to ensure two-digit representation
    hex += hexValue?.padStart(2, '0');
  }

  return hex;
};
export function getLineItemDescription(
  lineItem: ISalesOrderResponseProduct,
): string {
  if (
    lineItem.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
    lineItem.Segment?.toLowerCase() === WARRANTY_SEGMENT
  ) {
    return PROGRESSIVE_LINE_DESC_WARRANTY;
  }
  if (
    lineItem.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
    lineItem.Segment?.toLowerCase() === DELIVERY_SEGMENT
  ) {
    return PROGRESSIVE_LINE_DESC_DELIVERY;
  }
  return lineItem.Description ?? '';
}
export function getLineItemType(lineItem: ISalesOrderResponseProduct): string {
  if (
    lineItem.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
    lineItem.Segment?.toLowerCase() === DELIVERY_SEGMENT
  ) {
    return PROGRESSIVE_LINE_TYPE_DELIVERY;
  }
  return PROGRESSIVE_LINE_TYPE_LEASE;
}
export function getLineItemSKU(lineItem: ISalesOrderResponseProduct): string {
  if (
    lineItem.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
    lineItem.Segment?.toLowerCase() === DELIVERY_SEGMENT
  ) {
    return PROGRESSIVE_LINE_SKU_DELIVERY;
  }
  return lineItem.ItemId ?? '';
}

export function getProgressiveNonLeasableItems(
  salesOrder: ISalesOrderResponse,
  blockedStatesForExtendWarranty: BlockedState[],
  nonLeasableItems: string | null | undefined,
): string[] {
  const items: string[] = [];
  const shippingAddressState = salesOrder?.ISOStateCode;
  const isBlockedStateForExtend = blockedStatesForExtendWarranty.some(
    (state: BlockedState) => state?.stateName === shippingAddressState,
  );
  // Check for warranty blocked states and warranty items in the order
  if (isBlockedStateForExtend) {
    const warrantyItems =
      salesOrder?.ProductList?.filter(
        (item) => item.Segment?.toLowerCase() === WARRANTY_SEGMENT,
      ).map((item) => item?.Description) || [];
    items.push(...warrantyItems);
  }

  // Check for donation items in the order
  const donationItems =
    salesOrder?.ProductList?.filter(
      (item) =>
        item?.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
        item?.Segment?.toLowerCase() === DONATION_SEGMENT,
    ).map((item) => item?.Description) || [];
  items.push(...donationItems);

  // Check for returned items (items with negative quantity)
  const returnedItems =
    salesOrder?.ProductList?.filter((item) => item?.Qty < 0).map(
      (item) => 'Return - ' + item?.Description,
    ) || [];
  items.push(...returnedItems);

  // Check for non-leasable items by ItemId from BSP config (e.g. exchange SKU)
  if (nonLeasableItems) {
    const nonLeasableItemsArray = nonLeasableItems
      .split(',')
      .map((item) => item.trim());
    const matchingNonLeasableItems =
      salesOrder?.ProductList?.filter((item) =>
        nonLeasableItemsArray.includes(item?.ItemId || ''),
      ).map((item) => item?.Description) || [];
    items.push(...matchingNonLeasableItems);
  }

  return items;
}
export function isDeliveryExcludedState(
  stateToExclude: string[] | null | undefined,
  shippingState: string | null | undefined,
): boolean {
  if (!stateToExclude || stateToExclude.length === 0 || !shippingState) {
    return false;
  }
  return stateToExclude.includes(shippingState);
}
